import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DialogClose } from '@/components/DialogClose';
import {
  useHrmsDuplicates, useMergeDuplicates,
  type DuplicateGroup, type MergeResult,
} from '@/api/hrms-sync';
import { errorMessage } from '@/lib/api-client';

/**
 * Live employees that are one uKnowva user (a changed employee code used to
 * create a new employee on every sync). Safe groups are merged by every
 * employee sync on its own; this window shows them and lets HR merge the rest,
 * choosing which record to keep. Copies are soft-deleted, never erased.
 */
export function DuplicatesDialog({ open, onClose, canEdit }: {
  open: boolean; onClose: () => void; canEdit: boolean;
}) {
  const { data, isLoading, error } = useHrmsDuplicates(open);
  const merge = useMergeDuplicates();
  const [keep, setKeep] = useState<Record<string, string>>({});
  const [result, setResult] = useState<MergeResult | null>(null);
  // What the confirm step will merge: null = every safe group.
  const [pending, setPending] = useState<{ keys: string[] | null } | null>(null);

  const groups = data?.groups ?? [];
  const autoCount = groups.filter((g) => g.auto).length;

  const run = (keys: string[] | null) => {
    setPending(null);
    setResult(null);
    merge.mutate({ keys, survivors: keep }, { onSuccess: setResult });
  };
  const pendingGroups = pending
    ? groups.filter((g) => (pending.keys ? pending.keys.includes(g.key) : g.auto))
    : [];
  const keptCode = (g: DuplicateGroup) =>
    g.members.find((m) => m.id === (keep[g.key] ?? g.survivor_id))?.emp_id ?? '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Duplicate employees
        <Typography variant="body2" color="text.secondary">
          One uKnowva user should be one employee in payroll. Merging keeps one record, moves
          the other records&apos; attendance and logins onto it, and retires the copies.
        </Typography>
        <DialogClose onClose={onClose} />
      </DialogTitle>
      <DialogContent dividers>
        {(isLoading || merge.isPending) && <LinearProgress sx={{ mb: 2 }} />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(error)}</Alert>}
        {merge.error && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(merge.error)}</Alert>}
        {result && (
          <Alert severity={result.skipped.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
            {result.merged.length
              ? result.merged.map((m) => `${m.merged.map((x) => x.emp_id).join(', ')} → ${m.survivor.emp_id}`).join('; ')
              : 'Nothing was merged.'}
            {result.skipped.map((s) => <div key={s.key}>Not merged ({s.key}): {s.reason}</div>)}
          </Alert>
        )}
        {data && data.staged_users === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No employees have been synced from uKnowva yet, so only records sharing a uKnowva id
            can be checked. Run Sync new data with Employees selected.
          </Alert>
        )}
        {data && !groups.length && !data.conflicts.length && (
          <Typography color="text.secondary">No duplicate employees found.</Typography>
        )}

        {canEdit && autoCount > 0 && (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Button variant="contained" disabled={merge.isPending} onClick={() => setPending({ keys: null })}>
              Merge all safe groups ({autoCount})
            </Button>
            <Typography variant="caption" color="text.secondary">
              The next employee sync would merge these anyway.
            </Typography>
          </Stack>
        )}

        <Stack spacing={2}>
          {groups.map((g) => (
            <GroupCard key={g.key} group={g} canEdit={canEdit} busy={merge.isPending}
              chosen={keep[g.key] ?? g.survivor_id}
              onChoose={(id) => setKeep((k) => ({ ...k, [g.key]: id }))}
              onMerge={() => setPending({ keys: [g.key] })} />
          ))}
        </Stack>

        {!!data?.conflicts.length && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>Records more than one uKnowva user points at</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              These are never merged automatically. Correct the employee code or email in uKnowva
              or in payroll so each record belongs to one person.
            </Typography>
            {data.conflicts.map((c) => (
              <Typography key={c.employee_id} variant="body2">
                <b>{c.emp_id}</b> {c.name}: matches {c.uknowva_users.join(', ')}
              </Typography>
            ))}
          </Box>
        )}
      </DialogContent>
      <ConfirmDialog
        open={!!pending}
        title={pendingGroups.length === 1 ? 'Merge these records?' : `Merge ${pendingGroups.length} groups?`}
        confirmLabel="Merge"
        busy={merge.isPending}
        onCancel={() => setPending(null)}
        onConfirm={() => run(pending?.keys ?? null)}
        description={(
          <>
            {pendingGroups.slice(0, 8).map((g) => (
              <Typography key={g.key} variant="body2">
                Keep <b>{keptCode(g)}</b>; retire{' '}
                {g.members.filter((m) => m.emp_id !== keptCode(g)).map((m) => m.emp_id).join(', ')}
              </Typography>
            ))}
            {pendingGroups.length > 8 && (
              <Typography variant="body2">…and {pendingGroups.length - 8} more.</Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Attendance and logins move to the record you keep. The retired records are
              soft-deleted and stay in the change log.
            </Typography>
          </>
        )}
      />
    </Dialog>
  );
}

function GroupCard({ group: g, chosen, onChoose, onMerge, canEdit, busy }: {
  group: DuplicateGroup; chosen: string; onChoose: (id: string) => void;
  onMerge: () => void; canEdit: boolean; busy: boolean;
}) {
  const who = [g.uknowva.name, g.uknowva.code, g.uknowva.email].filter(Boolean).join(' · ');
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography variant="subtitle2">uKnowva user: {who || g.key}</Typography>
        {g.auto
          ? <Chip size="small" color="success" variant="outlined" label="Safe: merged on next sync" />
          : <Chip size="small" color="warning" variant="outlined" label="Needs review" />}
        <Box sx={{ flex: 1 }} />
        {canEdit && (
          <Button size="small" variant="outlined" disabled={busy || !g.mergeable} onClick={onMerge}>
            Merge into selected
          </Button>
        )}
      </Stack>
      {g.reasons.map((r) => (
        <Typography key={r} variant="caption" color="warning.main" sx={{ display: 'block' }}>{r}</Typography>
      ))}
      <TableContainer><Table size="small" sx={{ mt: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">Keep</TableCell>
            <TableCell>Code</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Company</TableCell>
            <TableCell>Created</TableCell>
            <TableCell align="right">Attendance days</TableCell>
            <TableCell align="right">Payroll lines</TableCell>
            <TableCell>Logins</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {g.members.map((m) => (
            <TableRow key={m.id} selected={m.id === chosen}>
              <TableCell padding="checkbox">
                <Radio size="small" checked={m.id === chosen} disabled={!canEdit}
                  onChange={() => onChoose(m.id)} />
              </TableCell>
              <TableCell sx={{ fontFamily: 'monospace' }}>{m.emp_id}</TableCell>
              <TableCell>{m.name}</TableCell>
              <TableCell>{m.entity ?? '—'}</TableCell>
              <TableCell>{m.created_at ? m.created_at.slice(0, 10) : '—'}</TableCell>
              <TableCell align="right">{m.attendance_days}</TableCell>
              <TableCell align="right">{m.payroll_lines}</TableCell>
              <TableCell>{m.logins.join(', ') || '—'}</TableCell>
              <TableCell>{m.date_of_leaving ? `Left ${m.date_of_leaving}` : m.status}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table></TableContainer>
    </Paper>
  );
}
